import { isPhotonAppRoute } from "@/components/Auth/shared/constants.auth";
import useAppNavigate from "@/hooks/useAppNavigate";
import { useLocation, useNavigate } from "@/lib/router";
import { useEffect, useRef, useState } from "react";
import { useSigninCheck } from "reactfire";
import Navbar from "..";
import { loggedInTabs, loggedOutTabs, resourcesDropdown } from "../config.nav";
import { toNavTabItems } from "../toNavTabItems";
import type { NavbarConfig, ResolvedNavConfig } from "../types";
import { NavCta, NavProfile } from "../ui";

const WithNavbar: React.FC<NavbarConfig> = ({
  variant,
  dark = true,
  sticky = true,
  hide = false,
  backPath,
  onBack,
  onLogoClick,
  hideCta = false,
}) => {
  // ── data: auth ──
  const { data: signInCheckResult } = useSigninCheck();
  const signedIn = signInCheckResult.signedIn;

  // ── data: scroll / theme ──
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const darkTheme = dark || (scrolled && sticky);

  // ── hooks for tab logic ──
  const navigate = useNavigate();
  const navigateTo = useAppNavigate();
  const location = useLocation();

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) =>
      setScrolled(!entry.isIntersecting),
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // ── resolve effective variant ──
  const effectiveVariant =
    variant === "strip"
      ? "strip"
      : variant === "logged-in" || (variant !== "logged-out" && signedIn)
        ? "logged-in"
        : "logged-out";

  // ── build config ──
  const config: ResolvedNavConfig = {
    dark: darkTheme,
    sticky,
    onLogoClick,
  };

  if (effectiveVariant === "strip") {
    config.backButton = {
      onClick: () => {
        if (backPath) return navigate(backPath);
        if (onBack) return onBack();
        navigate(-1);
      },
    };

    if (!hideCta) {
      config.rightSlot = signedIn ? (
        <NavProfile />
      ) : (
        <NavCta
          onClick={() =>
            navigate(`/auth?redirect=${location.pathname.slice(1)}`)
          }
        >
          Get Aftershoot
        </NavCta>
      );
    }
  } else if (effectiveVariant === "logged-in") {
    // Photon funnel context: no Aftershoot nav tabs, just the logo and the
    // (already Photon-scoped) profile menu. The mobile menu stays available
    // but swaps its items for the same General/Help/Log out set.
    const isPhotonFlow = isPhotonAppRoute(location.pathname);

    if (!isPhotonFlow) {
      config.tabs = toNavTabItems(loggedInTabs, {
        pathname: location.pathname,
        onNavigate: navigateTo,
      });
    }

    config.rightSlot = <NavProfile />;

    config.showHamburger = true;
    config.showMobileMenu = true;
    config.isPhotonFlow = isPhotonFlow;
  } else {
    // logged-out
    config.tabs = toNavTabItems(loggedOutTabs, {
      pathname: location.pathname,
    });

    config.resourcesDropdown = toNavTabItems(resourcesDropdown);

    config.rightSlot = (
      <NavCta onClick={() => navigate("/auth")} className="px-8">
        Get Started
      </NavCta>
    );

    config.showHamburger = true;
    config.showMobileMenu = true;
  }

  if (hide) return null;

  return (
    <>
      <div role="row" ref={sentinelRef} className="absolute top-0 h-0" />
      <Navbar {...config} />
    </>
  );
};

export default WithNavbar;
